import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useRecoilState } from 'recoil';
import styled from '@emotion/styled';
import { ToastState, toastState } from '@/state/toastState';
import { FORM_COLOR } from '@/pages/Seminar/components/form/formStyles';

/**
 * 바닥에서 띄우는 높이 — 화면 아래에 입력줄이 붙는 화면(지원 대화방)은 body 에 `--toast-bottom` 을 걸어 입력줄 위로 올린다.
 * 값이 없으면 하단 탭 위(80px + 아이폰 아래 안전 여백).
 */
const TOAST_BOTTOM: string = 'var(--toast-bottom, calc(80px + env(safe-area-inset-bottom)))';

const ToastContainer = styled.div<{ show: boolean; type: ToastState['type'] }>`
    position: fixed;
    bottom: ${TOAST_BOTTOM};
    left: 50%;
    transform: translateX(-50%);
    padding: 10px 20px;
    border-radius: 5px;
    color: white;
    background-color: ${({ type }) => {
        switch (type) {
            case 'success':
                return 'green';
            case 'error':
                // UI 가이드 1장 — 오류 빨강은 한 가지(#E11D48)
                return FORM_COLOR.danger;
            case 'warning':
                return 'orange';
            default:
                return 'black';
        }
    }};
    opacity: ${({ show }) => (show ? 1 : 0)};
    visibility: ${({ show }) => (show ? 'visible' : 'hidden')};
    transition: opacity 0.3s, visibility 0.3s;
    z-index: 1500;
    /* left 50% 로 놓으면 폭이 화면 절반으로 묶여 짧은 문장도 꺾인다 — 내용 폭만큼 넓히고 화면 좌우 16px 은 남긴다 */
    width: max-content;
    max-width: calc(100vw - 32px);
    box-sizing: border-box;
    /* 문장마다 줄을 바꿔 보여 준다([toastLines]) — 어절 중간에서는 끊지 않는다 */
    white-space: pre-line;
    word-break: keep-all;
    text-align: center;
    line-height: 1.45;

    @media (max-width: 768px) {
        bottom: ${TOAST_BOTTOM};
        padding: 8px 16px;
        font-size: 14px;
    }
`;

/**
 * 토스트 문구를 문장마다 줄바꿈한다(2026-10-02 사용자 지시 — 두 문장이 한 줄에 붙어 길게 나오지 않게).
 * 문장 끝(. ! ?) 뒤 공백을 줄바꿈으로 바꾼다. 서버가 내려 준 오류 문구도 같은 규칙으로 나뉜다.
 * 문구에 직접 넣은 줄바꿈(\n)은 그대로 둔다. "3.5점"처럼 뒤에 공백이 없는 점은 건드리지 않는다.
 */
const toastLines = (message: string): string =>
    message.replace(/([.!?])[ \t]+(?=\S)/g, '$1\n').trim();

/** 하단 고정 줄 사이가 이만큼 안이면 붙어 쌓인 줄로 본다 */
const STACK_GAP_PX: number = 24;
/** 하단 고정 줄 위로 띄우는 간격 */
const BOTTOM_BAR_GAP_PX: number = 12;

/**
 * 화면 아래에 붙어 있는 고정 줄(대화방 [불합격] 줄·폼 [이전][다음]·상세 [지원하기] 등) 중 가장 높이 올라온 줄의 화면 아래에서 높이 — 화면에서 잰다.
 * 토스트가 그 줄을 가리지 않게 이 높이 위로 띄운다(2026-10-07 대화방 [불합격] 줄을 토스트가 잠깐 가렸다). 숨긴 줄(입력 중 visibility:hidden)은 뺀다.
 */
const bottomBarsHeight = (exclude: Element | null): number => {
    const viewHeight: number = window.innerHeight;
    const centerX: number = window.innerWidth / 2;
    const bars: DOMRect[] = [];
    document.body.querySelectorAll<HTMLElement>('*').forEach((el: HTMLElement) => {
        if (exclude?.contains(el)) return;
        const style: CSSStyleDeclaration = window.getComputedStyle(el);
        if (style.position !== 'fixed' && style.position !== 'sticky') return;
        if (style.visibility === 'hidden' || style.display === 'none' || parseFloat(style.opacity) === 0) return;
        const rect: DOMRect = el.getBoundingClientRect();
        if (rect.height <= 0 || rect.height > viewHeight / 3 || rect.top < viewHeight / 2) return;
        if (rect.left > centerX || rect.right < centerX) return;
        bars.push(rect);
    });
    // 화면 맨 아래부터 위로, 붙어 쌓인 줄(하단 탭 위 [지원하기] 줄 등)을 차례로 넘는다
    let edge: number = viewHeight;
    for (let guard: number = 0; guard < 4; guard += 1) {
        const next: number = Math.min(edge, ...bars.filter((rect: DOMRect) => rect.bottom >= edge - STACK_GAP_PX && rect.top < edge).map((rect: DOMRect) => rect.top));
        if (next >= edge) break;
        edge = next;
    }
    return Math.max(0, Math.round(viewHeight - edge));
};

const Toast = () => {
    const [toast, setToast] = useRecoilState(toastState);

    useEffect(() => {
        if (toast.show) {
            const timer = setTimeout(() => {
                setToast((prev) => ({ ...prev, show: false }));
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [toast.show, setToast]);

    // 뜰 때 하단 고정 줄 높이를 재서 그 위로(기본 높이·대화방 입력줄 위 --toast-bottom 보다 낮아지지는 않는다)
    const containerRef = useRef<HTMLDivElement>(null);
    const [barsHeight, setBarsHeight] = useState<number>(0);
    useLayoutEffect(() => {
        if (toast.show) setBarsHeight(bottomBarsHeight(containerRef.current));
    }, [toast.show, toast.message]);

    return (
        <ToastContainer
            ref={containerRef}
            show={toast.show}
            type={toast.type}
            style={barsHeight > 0 ? { bottom: `max(${TOAST_BOTTOM}, ${barsHeight + BOTTOM_BAR_GAP_PX}px)` } : undefined}
        >
            {toastLines(toast.message)}
        </ToastContainer>
    );
};

export default Toast;
